import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRotate, faClockRotateLeft, faDollarSign, faGear, faWallet } from '@fortawesome/free-solid-svg-icons';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import swal from 'sweetalert';
import { getAuth } from 'firebase/auth';
import { addDoc, collection, doc, getDoc, getDocs, limit, orderBy, query, setDoc, Timestamp } from 'firebase/firestore';
import { getFirestore } from '@firebase/firestore';
import { app } from '../firebase.js';
import { logAdminActivity } from '../utils/activityLogger';
import { paybillApi } from '../services/paybillApi';

const CURRENCY_SETTINGS_COLLECTION = 'Settings';
const CURRENCY_SETTINGS_DOC = 'currency';
const ADMISSION_FEE_SETTINGS_DOC = 'admissionFee';
const FEE_PAYMENT_SETTINGS_DOC = 'feePaymentDetails';
const EXCHANGE_RATE_HISTORY_COLLECTION = 'ExchangeRateHistory';
const ADMISSION_FEE_HISTORY_COLLECTION = 'AdmissionFeeSettingsHistory';
const FEE_PAYMENT_HISTORY_COLLECTION = 'FeePaymentDetailsHistory';
const DEFAULT_USD_TO_KES_RATE = 130;
const DEFAULT_ADMISSION_FEE_AMOUNT = 2000;

function MpesaSecurityCredentialSettings() {
  const [credentials, setCredentials] = useState({ balance: '', b2c: '' });
  const [savingService, setSavingService] = useState('');
  const [messages, setMessages] = useState({});

  const updateCredential = (service, value) => {
    setCredentials((current) => ({ ...current, [service]: value }));
    setMessages((current) => ({ ...current, [service]: '' }));
  };

  const saveCredential = async (service) => {
    const securityCredential = credentials[service].trim();
    const label = service === 'balance' ? 'Balance API' : 'B2C payouts';
    if (securityCredential.length < 32) {
      setMessages((current) => ({ ...current, [service]: 'Paste the complete M-Pesa security credential first.' }));
      return;
    }
    if (!window.confirm(`Replace the ${label} security credential in Secret Manager? New requests will use it immediately.`)) return;

    setSavingService(service);
    setMessages((current) => ({ ...current, [service]: '' }));
    try {
      const result = await paybillApi('/api/settings/mpesa-security-credential', {
        method: 'POST',
        body: { service, securityCredential },
      });
      setCredentials((current) => ({ ...current, [service]: '' }));
      setMessages((current) => ({ ...current, [service]: result.message || 'Saved to Secret Manager.' }));
    } catch (error) {
      setMessages((current) => ({ ...current, [service]: error.message || 'Could not update the credential.' }));
    } finally {
      setSavingService('');
    }
  };

  const credentialFields = [
    { id: 'balance', title: 'Balance API security credential', description: 'Used when requesting the Paybill account balance.' },
    { id: 'b2c', title: 'B2C security credential', description: 'Used when submitting M-Pesa withdrawals.' },
  ];

  return (
    <div className="settingsCard settingsMpesaCredentialCard">
      <div className="settingsCardHeader">
        <div className="settingsIconBubble settingsIconBubbleMuted">
          <FontAwesomeIcon icon={faGear} />
        </div>
        <div>
          <h5>M-Pesa security credentials</h5>
          <p>Paste a rotated Daraja SecurityCredential and save it directly to Google Secret Manager.</p>
        </div>
      </div>
      <p className="settingsMpesaCredentialNote">Credentials are write-only here. The page never loads or displays the saved values.</p>
      <div className="settingsMpesaCredentialGrid">
        {credentialFields.map((field) => (
          <div className="settingsMpesaCredentialField" key={field.id}>
            <Form.Group>
              <Form.Label htmlFor={`mpesa-credential-${field.id}`}>{field.title}</Form.Label>
              <Form.Control
                id={`mpesa-credential-${field.id}`}
                type="password"
                autoComplete="new-password"
                value={credentials[field.id]}
                onChange={(event) => updateCredential(field.id, event.target.value)}
                placeholder="Paste the new credential"
              />
              <Form.Text className="text-muted">{field.description}</Form.Text>
            </Form.Group>
            <Button
              variant="dark"
              type="button"
              onClick={() => saveCredential(field.id)}
              disabled={Boolean(savingService)}
            >
              {savingService === field.id ? 'Saving securely…' : 'Save credential'}
            </Button>
            {messages[field.id] && <p className="settingsMpesaCredentialMessage" role="status">{messages[field.id]}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

const createPaymentItemId = (prefix) => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const normalizeBanks = (items = []) => {
  if (!Array.isArray(items)) return [];

  return items.map((item) => ({
    id: String(item?.id || createPaymentItemId('bank')),
    bankName: String(item?.bankName || ''),
    accountName: String(item?.accountName || ''),
    accountNumber: String(item?.accountNumber || ''),
    note: String(item?.note || '')
  }));
};

const normalizePaybills = (items = []) => {
  if (!Array.isArray(items)) return [];

  return items.map((item) => ({
    id: String(item?.id || createPaymentItemId('paybill')),
    paybillNumber: String(item?.paybillNumber || ''),
    accountNumber: String(item?.accountNumber || ''),
    note: String(item?.note || '')
  }));
};

const normalizeTills = (items = []) => {
  if (!Array.isArray(items)) return [];

  return items.map((item) => ({
    id: String(item?.id || createPaymentItemId('till')),
    tillNumber: String(item?.tillNumber || ''),
    note: String(item?.note || '')
  }));
};

const formatNumber = (value, maximumFractionDigits = 4) => {
  const number = Number(value || 0);
  if (!Number.isFinite(number)) return '0';

  return number.toLocaleString('en-KE', {
    maximumFractionDigits
  });
};

const formatDateTime = (value) => {
  if (!value) return 'Not updated yet';

  const dateObj = value?.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(dateObj.getTime())) return 'Not updated yet';

  return dateObj.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

function SettingsComp() {
  const db = getFirestore(app);
  const auth = getAuth();

  const [activeSection, setActiveSection] = useState('currency');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [admissionFeeSettings, setAdmissionFeeSettings] = useState(null);
  const [paymentSettings, setPaymentSettings] = useState(null);
  const [usdToKesRate, setUsdToKesRate] = useState('');
  const [admissionFeeAmount, setAdmissionFeeAmount] = useState('');
  const [rateNote, setRateNote] = useState('');
  const [admissionFeeNote, setAdmissionFeeNote] = useState('');
  const [history, setHistory] = useState([]);
  const [savingAdmissionFee, setSavingAdmissionFee] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [paybills, setPaybills] = useState([]);
  const [tills, setTills] = useState([]);
  const [savingPaymentDetails, setSavingPaymentDetails] = useState(false);

  const totalPaymentEntries = useMemo(
    () => bankAccounts.length + paybills.length + tills.length,
    [bankAccounts.length, paybills.length, tills.length]
  );

  const numericUsdToKesRate = useMemo(() => Number(usdToKesRate), [usdToKesRate]);
  const numericAdmissionFeeAmount = useMemo(() => Number(admissionFeeAmount), [admissionFeeAmount]);
  const validRate = Number.isFinite(numericUsdToKesRate) && numericUsdToKesRate > 0;
  const validAdmissionFee = Number.isFinite(numericAdmissionFeeAmount) && numericAdmissionFeeAmount > 0;
  const kesToUsdRate = validRate ? 1 / numericUsdToKesRate : 0;
  const sampleKes = 100000;
  const sampleUsd = 1000;

  const fetchSettings = useCallback(async () => {
    setLoading(true);

    try {
      const [settingsSnap, admissionFeeSnap, paymentDetailsSnap, historySnap] = await Promise.all([
        getDoc(doc(db, CURRENCY_SETTINGS_COLLECTION, CURRENCY_SETTINGS_DOC)),
        getDoc(doc(db, CURRENCY_SETTINGS_COLLECTION, ADMISSION_FEE_SETTINGS_DOC)),
        getDoc(doc(db, CURRENCY_SETTINGS_COLLECTION, FEE_PAYMENT_SETTINGS_DOC)),
        getDocs(query(
          collection(db, EXCHANGE_RATE_HISTORY_COLLECTION),
          orderBy('createdAt', 'desc'),
          limit(8)
        ))
      ]);

      const settingsData = settingsSnap.exists() ? settingsSnap.data() : null;
      const admissionFeeData = admissionFeeSnap.exists() ? admissionFeeSnap.data() : null;
      const paymentDetailsData = paymentDetailsSnap.exists() ? paymentDetailsSnap.data() : null;
      setSettings(settingsData);
      setAdmissionFeeSettings(admissionFeeData);
      setPaymentSettings(paymentDetailsData);
      setUsdToKesRate(String(settingsData?.usdToKesRate || DEFAULT_USD_TO_KES_RATE));
      setAdmissionFeeAmount(String(admissionFeeData?.amountKes || DEFAULT_ADMISSION_FEE_AMOUNT));
      setBankAccounts(normalizeBanks(paymentDetailsData?.bankAccounts || paymentDetailsData?.banks || []));
      setPaybills(normalizePaybills(paymentDetailsData?.paybills || paymentDetailsData?.paybillNumbers || []));
      setTills(normalizeTills(paymentDetailsData?.tills || paymentDetailsData?.tillNumbers || []));

      const historyItems = [];
      historySnap.forEach((historyDoc) => {
        historyItems.push({ id: historyDoc.id, ...historyDoc.data() });
      });
      setHistory(historyItems);
    } catch (error) {
      console.error('Error loading settings:', error);
      swal('Error', 'Failed to load settings. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  }, [db]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const saveCurrencySettings = useCallback(async () => {
    if (!validRate) {
      swal('Invalid rate', 'Enter a valid exchange rate greater than zero.', 'warning');
      return;
    }

    const currentUser = auth.currentUser;
    if (!currentUser) {
      swal('Session expired', 'Please log in again before updating settings.', 'error');
      return;
    }

    setSaving(true);

    const now = Timestamp.now();
    const payload = {
      baseCurrency: 'KES',
      quoteCurrency: 'USD',
      usdToKesRate: numericUsdToKesRate,
      kesToUsdRate: Number(kesToUsdRate.toFixed(8)),
      updatedAt: now,
      updatedBy: currentUser.uid,
      updatedByEmail: currentUser.email || '',
      note: rateNote.trim()
    };

    try {
      await Promise.all([
        setDoc(doc(db, CURRENCY_SETTINGS_COLLECTION, CURRENCY_SETTINGS_DOC), payload, { merge: true }),
        addDoc(collection(db, EXCHANGE_RATE_HISTORY_COLLECTION), {
          ...payload,
          createdAt: now
        })
      ]);

      setSettings(payload);
      setRateNote('');
      await fetchSettings();

      logAdminActivity({
        action: 'Update Settings',
        details: `Updated USD to KES exchange rate to ${numericUsdToKesRate}`,
        meta: {
          page: 'Settings',
          setting: 'currency_exchange_rate',
          usdToKesRate: numericUsdToKesRate,
          kesToUsdRate: Number(kesToUsdRate.toFixed(8))
        }
      });

      swal('Settings updated', 'The exchange rate has been saved successfully.', 'success');
    } catch (error) {
      console.error('Error saving currency settings:', error);
      swal('Error', 'Failed to save the exchange rate. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  }, [auth, db, fetchSettings, kesToUsdRate, numericUsdToKesRate, rateNote, validRate]);

  const saveAdmissionFeeSettings = useCallback(async () => {
    if (!validAdmissionFee) {
      swal('Invalid fee', 'Enter a valid admission fee greater than zero.', 'warning');
      return;
    }

    const currentUser = auth.currentUser;
    if (!currentUser) {
      swal('Session expired', 'Please log in again before updating settings.', 'error');
      return;
    }

    setSavingAdmissionFee(true);

    const now = Timestamp.now();
    const payload = {
      amountKes: numericAdmissionFeeAmount,
      currency: 'KES',
      updatedAt: now,
      updatedBy: currentUser.uid,
      updatedByEmail: currentUser.email || '',
      note: admissionFeeNote.trim()
    };

    try {
      await Promise.all([
        setDoc(doc(db, CURRENCY_SETTINGS_COLLECTION, ADMISSION_FEE_SETTINGS_DOC), payload, { merge: true }),
        addDoc(collection(db, ADMISSION_FEE_HISTORY_COLLECTION), {
          ...payload,
          createdAt: now
        })
      ]);

      setAdmissionFeeSettings(payload);
      setAdmissionFeeNote('');

      logAdminActivity({
        action: 'Update Settings',
        details: `Updated admission fee to KES ${numericAdmissionFeeAmount}`,
        meta: {
          page: 'Settings',
          setting: 'admission_fee_amount',
          amountKes: numericAdmissionFeeAmount
        }
      });

      swal('Settings updated', 'The admission fee has been saved successfully.', 'success');
    } catch (error) {
      console.error('Error saving admission fee settings:', error);
      swal('Error', 'Failed to save the admission fee. Please try again.', 'error');
    } finally {
      setSavingAdmissionFee(false);
    }
  }, [admissionFeeNote, auth, db, numericAdmissionFeeAmount, validAdmissionFee]);

  const addBankAccount = useCallback(() => {
    setBankAccounts((previous) => ([
      ...previous,
      {
        id: createPaymentItemId('bank'),
        bankName: '',
        accountName: '',
        accountNumber: '',
        note: ''
      }
    ]));
  }, []);

  const addPaybill = useCallback(() => {
    setPaybills((previous) => ([
      ...previous,
      {
        id: createPaymentItemId('paybill'),
        paybillNumber: '',
        accountNumber: '',
        note: ''
      }
    ]));
  }, []);

  const addTill = useCallback(() => {
    setTills((previous) => ([
      ...previous,
      {
        id: createPaymentItemId('till'),
        tillNumber: '',
        note: ''
      }
    ]));
  }, []);

  const updateBankAccount = useCallback((id, field, value) => {
    setBankAccounts((previous) => previous.map((item) => (
      item.id === id ? { ...item, [field]: value } : item
    )));
  }, []);

  const updatePaybill = useCallback((id, field, value) => {
    setPaybills((previous) => previous.map((item) => (
      item.id === id ? { ...item, [field]: value } : item
    )));
  }, []);

  const updateTill = useCallback((id, field, value) => {
    setTills((previous) => previous.map((item) => (
      item.id === id ? { ...item, [field]: value } : item
    )));
  }, []);

  const removeBankAccount = useCallback((id) => {
    setBankAccounts((previous) => previous.filter((item) => item.id !== id));
  }, []);

  const removePaybill = useCallback((id) => {
    setPaybills((previous) => previous.filter((item) => item.id !== id));
  }, []);

  const removeTill = useCallback((id) => {
    setTills((previous) => previous.filter((item) => item.id !== id));
  }, []);

  const savePaymentDetails = useCallback(async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      swal('Session expired', 'Please log in again before updating payment details.', 'error');
      return;
    }

    const cleanedBanks = bankAccounts
      .map((item) => ({
        id: item.id,
        bankName: String(item.bankName || '').trim(),
        accountName: String(item.accountName || '').trim(),
        accountNumber: String(item.accountNumber || '').trim(),
        note: String(item.note || '').trim()
      }))
      .filter((item) => item.bankName || item.accountNumber || item.note || item.accountName);

    const cleanedPaybills = paybills
      .map((item) => ({
        id: item.id,
        paybillNumber: String(item.paybillNumber || '').trim(),
        accountNumber: String(item.accountNumber || '').trim(),
        note: String(item.note || '').trim()
      }))
      .filter((item) => item.paybillNumber || item.accountNumber || item.note);

    const cleanedTills = tills
      .map((item) => ({
        id: item.id,
        tillNumber: String(item.tillNumber || '').trim(),
        note: String(item.note || '').trim()
      }))
      .filter((item) => item.tillNumber || item.note);

    if (!cleanedBanks.length && !cleanedPaybills.length && !cleanedTills.length) {
      swal('Nothing to save', 'Add at least one bank account, paybill number, or till number first.', 'warning');
      return;
    }

    setSavingPaymentDetails(true);

    const now = Timestamp.now();
    const payload = {
      bankAccounts: cleanedBanks,
      paybills: cleanedPaybills,
      tills: cleanedTills,
      updatedAt: now,
      updatedBy: currentUser.uid,
      updatedByEmail: currentUser.email || ''
    };

    try {
      await Promise.all([
        setDoc(doc(db, CURRENCY_SETTINGS_COLLECTION, FEE_PAYMENT_SETTINGS_DOC), payload, { merge: true }),
        addDoc(collection(db, FEE_PAYMENT_HISTORY_COLLECTION), {
          ...payload,
          createdAt: now
        })
      ]);

      setPaymentSettings(payload);
      setBankAccounts(cleanedBanks);
      setPaybills(cleanedPaybills);
      setTills(cleanedTills);

      logAdminActivity({
        action: 'Update Settings',
        details: 'Updated fee payment details',
        meta: {
          page: 'Settings',
          setting: 'fee_payment_details',
          bankAccounts: cleanedBanks.length,
          paybills: cleanedPaybills.length,
          tills: cleanedTills.length
        }
      });

      swal('Settings updated', 'The fee payment details have been saved successfully.', 'success');
    } catch (error) {
      console.error('Error saving fee payment details:', error);
      swal('Error', 'Failed to save the fee payment details. Please try again.', 'error');
    } finally {
      setSavingPaymentDetails(false);
    }
  }, [auth, bankAccounts, db, paybills, tills]);

  return (
    <div className="clientsComp applicationsPage settingsPage">
      <header className="settingsPageHeader">
        <div>
          <p className="settingsEyebrow">ADMINISTRATION</p>
          <h1>Settings</h1>
          <p>Manage shared finance values and payment information.</p>
        </div>
        <Button
          variant="outline-dark"
          className="settingsRefreshBtn"
          onClick={fetchSettings}
          disabled={loading || saving || savingAdmissionFee || savingPaymentDetails}
        >
          <FontAwesomeIcon icon={faArrowsRotate} />
          Refresh
        </Button>
      </header>

      <div className="settingsOverview" aria-label="Current settings overview">
        <div><span>Exchange rate</span><strong>{loading ? 'Loading…' : `1 USD = KES ${formatNumber(settings?.usdToKesRate || numericUsdToKesRate, 2)}`}</strong></div>
        <div><span>Admission fee</span><strong>{loading ? 'Loading…' : `KES ${formatNumber(admissionFeeSettings?.amountKes || numericAdmissionFeeAmount, 0)}`}</strong></div>
        <div><span>Payment methods</span><strong>{loading ? 'Loading…' : `${totalPaymentEntries} configured`}</strong></div>
        <div><span>Last rate update</span><strong>{loading ? 'Loading…' : formatDateTime(settings?.updatedAt)}</strong></div>
      </div>

      <nav className="settingsSectionNav" role="tablist" aria-label="Settings sections">
        {[
          { id: 'currency', label: 'Exchange rate' },
          { id: 'admission', label: 'Admission fee' },
          { id: 'payments', label: 'Payment details' },
          { id: 'security', label: 'M-Pesa security' },
          { id: 'history', label: 'Rate history' }
        ].map((section) => (
          <button
            key={section.id}
            id={`settings-tab-${section.id}`}
            type="button"
            role="tab"
            aria-selected={activeSection === section.id}
            aria-controls={`settings-panel-${section.id}`}
            tabIndex={activeSection === section.id ? 0 : -1}
            className={activeSection === section.id ? 'is-active' : ''}
            onClick={() => setActiveSection(section.id)}
            onKeyDown={(event) => {
              const sectionIds = ['currency', 'admission', 'payments', 'security', 'history'];
              const currentIndex = sectionIds.indexOf(section.id);
              const nextIndex = event.key === 'ArrowRight'
                ? (currentIndex + 1) % sectionIds.length
                : event.key === 'ArrowLeft'
                  ? (currentIndex - 1 + sectionIds.length) % sectionIds.length
                  : event.key === 'Home'
                    ? 0
                    : event.key === 'End'
                      ? sectionIds.length - 1
                      : -1;

              if (nextIndex >= 0) {
                event.preventDefault();
                const nextSection = sectionIds[nextIndex];
                setActiveSection(nextSection);
                document.getElementById(`settings-tab-${nextSection}`)?.focus();
              }
            }}
          >
            {section.label}
          </button>
        ))}
      </nav>

      {loading ? (
        <div className="settingsLoadingState">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading settings...</span>
          </Spinner>
        </div>
      ) : (
        <>
          <section
            className="settingsGrid settingsTabPanel"
            id="settings-panel-currency"
            role="tabpanel"
            aria-labelledby="settings-tab-currency"
            hidden={activeSection !== 'currency'}
          >
            <div className="settingsCard">
              <div className="settingsCardHeader">
                <div className="settingsIconBubble">
                  <FontAwesomeIcon icon={faDollarSign} />
                </div>
                <div>
                  <h5>Currency Exchange Rate</h5>
                  <p>Update the rate used when converting between KES and USD.</p>
                </div>
              </div>

              <div className="settingsFormGrid">
                <Form.Group>
                  <Form.Label>USD to KES rate</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    step="0.01"
                    value={usdToKesRate}
                    onChange={(event) => setUsdToKesRate(event.target.value)}
                    placeholder="e.g. 130"
                  />
                  <Form.Text className="text-muted">
                    Enter how many Kenyan shillings equal 1 US dollar.
                  </Form.Text>
                </Form.Group>

                <Form.Group>
                  <Form.Label>Optional note</Form.Label>
                  <Form.Control
                    type="text"
                    value={rateNote}
                    onChange={(event) => setRateNote(event.target.value)}
                    placeholder="e.g. Updated from bank rate"
                  />
                  <Form.Text className="text-muted">
                    This note is saved in the exchange-rate history.
                  </Form.Text>
                </Form.Group>
              </div>

              <div className="settingsPreviewCard">
                <p>Live preview</p>
                <div>
                  <span>KES {formatNumber(sampleKes, 0)} = USD {validRate ? formatNumber(sampleKes / numericUsdToKesRate, 2) : '0'}</span>
                  <span>USD {formatNumber(sampleUsd, 0)} = KES {validRate ? formatNumber(sampleUsd * numericUsdToKesRate, 0) : '0'}</span>
                </div>
              </div>

              <div className="settingsActions">
                <Button variant="dark" onClick={saveCurrencySettings} disabled={saving}>
                  {saving ? 'Saving...' : 'Save exchange rate'}
                </Button>
              </div>
            </div>

          </section>

          <section
            className="settingsTabPanel"
            id="settings-panel-admission"
            role="tabpanel"
            aria-labelledby="settings-tab-admission"
            hidden={activeSection !== 'admission'}
          >
            <div className="settingsCard">
              <div className="settingsCardHeader">
                <div className="settingsIconBubble">
                  <FontAwesomeIcon icon={faWallet} />
                </div>
                <div>
                  <h5>Admission Fee</h5>
                  <p>Set the admission fee used by Admin and the Students Portal.</p>
                </div>
              </div>

              <div className="settingsFormGrid">
                <Form.Group>
                  <Form.Label>Admission fee amount</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    step="1"
                    value={admissionFeeAmount}
                    onChange={(event) => setAdmissionFeeAmount(event.target.value)}
                    placeholder="e.g. 2000"
                  />
                  <Form.Text className="text-muted">
                    Amount is saved in KES and reused when recording or paying admission fees.
                  </Form.Text>
                </Form.Group>

                <Form.Group>
                  <Form.Label>Optional note</Form.Label>
                  <Form.Control
                    type="text"
                    value={admissionFeeNote}
                    onChange={(event) => setAdmissionFeeNote(event.target.value)}
                    placeholder="e.g. Updated for May intake"
                  />
                  <Form.Text className="text-muted">
                    This note is saved in the admission-fee settings history.
                  </Form.Text>
                </Form.Group>
              </div>

              <div className="settingsPreviewCard">
                <p>Current shared value</p>
                <div>
                  <span>KES {formatNumber(validAdmissionFee ? numericAdmissionFeeAmount : DEFAULT_ADMISSION_FEE_AMOUNT, 0)}</span>
                  <span>Last updated: {formatDateTime(admissionFeeSettings?.updatedAt)}</span>
                </div>
              </div>

              <div className="settingsActions">
                <Button variant="dark" onClick={saveAdmissionFeeSettings} disabled={savingAdmissionFee}>
                  {savingAdmissionFee ? 'Saving...' : 'Save admission fee'}
                </Button>
              </div>
            </div>

          </section>

          <section
            className="settingsTabPanel"
            id="settings-panel-history"
            role="tabpanel"
            aria-labelledby="settings-tab-history"
            hidden={activeSection !== 'history'}
          >
            <div className="settingsCard settingsHistoryCard">
              <div className="settingsCardHeader">
                <div className="settingsIconBubble settingsIconBubbleMuted">
                  <FontAwesomeIcon icon={faClockRotateLeft} />
                </div>
                <div>
                  <h5>Recent Updates</h5>
                  <p>Last exchange-rate changes for quick audit visibility.</p>
                </div>
              </div>

              {history.length === 0 ? (
                <div className="settingsEmptyState">
                  No exchange-rate updates recorded yet.
                </div>
              ) : (
                <div className="settingsHistoryList">
                  {history.map((item) => (
                    <div className="settingsHistoryItem" key={item.id}>
                      <div>
                        <strong>1 USD = KES {formatNumber(item.usdToKesRate, 2)}</strong>
                        <span>{item.updatedByEmail || 'Unknown admin'} • {formatDateTime(item.createdAt || item.updatedAt)}</span>
                        {item.note && <p>{item.note}</p>}
                      </div>
                      <FontAwesomeIcon icon={faGear} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>

          <section
            className="settingsTabPanel"
            id="settings-panel-payments"
            role="tabpanel"
            aria-labelledby="settings-tab-payments"
            hidden={activeSection !== 'payments'}
          >
          <div className="settingsCard settingsPaymentCard">
            <div className="settingsCardHeader">
              <div className="settingsIconBubble settingsIconBubbleMuted">
                <FontAwesomeIcon icon={faWallet} />
              </div>
              <div>
                <h5>Fee Payment Details</h5>
                <p>Add the bank accounts, paybill numbers, and till numbers used for fee payment and other purposes.</p>
              </div>
            </div>

            <div className="settingsPreviewCard settingsPaymentSummary">
              <p>Saved payment paths</p>
              <div>
                <span>Banks {bankAccounts.length}</span>
                <span>Paybills {paybills.length}</span>
                <span>Tills {tills.length}</span>
                <span>Updated {formatDateTime(paymentSettings?.updatedAt)}</span>
              </div>
            </div>

            <div className="settingsPaymentSections">
              <div className="settingsPaymentSection">
                <div className="settingsPaymentSectionHeader">
                  <div>
                    <h6>Bank Accounts</h6>
                    <p>Use for bank transfers, tuition, and other tracked deposits.</p>
                  </div>
                  <Button variant="outline-dark" size="sm" onClick={addBankAccount}>
                    Add bank
                  </Button>
                </div>

                <div className="settingsPaymentList">
                  {bankAccounts.length === 0 ? (
                    <div className="settingsEmptyState settingsEmptyState--compact">
                      No bank accounts added yet.
                    </div>
                  ) : bankAccounts.map((item) => (
                    <div className="settingsPaymentRow" key={item.id}>
                      <div className="settingsPaymentRowGrid">
                        <Form.Group>
                          <Form.Label>Bank name</Form.Label>
                          <Form.Control
                            type="text"
                            value={item.bankName}
                            onChange={(event) => updateBankAccount(item.id, 'bankName', event.target.value)}
                            placeholder="e.g. KCB Bank"
                          />
                        </Form.Group>
                        <Form.Group>
                          <Form.Label>Account name</Form.Label>
                          <Form.Control
                            type="text"
                            value={item.accountName}
                            onChange={(event) => updateBankAccount(item.id, 'accountName', event.target.value)}
                            placeholder="e.g. UBUNIFU COLLEGE"
                          />
                        </Form.Group>
                        <Form.Group>
                          <Form.Label>Account number</Form.Label>
                          <Form.Control
                            type="text"
                            value={item.accountNumber}
                            onChange={(event) => updateBankAccount(item.id, 'accountNumber', event.target.value)}
                            placeholder="e.g. 1234567890"
                          />
                        </Form.Group>
                        <Form.Group>
                          <Form.Label>Note</Form.Label>
                          <Form.Control
                            type="text"
                            value={item.note}
                            onChange={(event) => updateBankAccount(item.id, 'note', event.target.value)}
                            placeholder="Fee payment, savings, etc."
                          />
                        </Form.Group>
                      </div>
                      <Button
                        variant="outline-danger"
                        className="settingsPaymentRemoveBtn"
                        onClick={() => removeBankAccount(item.id)}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="settingsPaymentSection">
                <div className="settingsPaymentSectionHeader">
                  <div>
                    <h6>Paybill Numbers</h6>
                    <p>Use for M-Pesa paybill collections and reference-based payments.</p>
                  </div>
                  <Button variant="outline-dark" size="sm" onClick={addPaybill}>
                    Add paybill
                  </Button>
                </div>

                <div className="settingsPaymentList">
                  {paybills.length === 0 ? (
                    <div className="settingsEmptyState settingsEmptyState--compact">
                      No paybill numbers added yet.
                    </div>
                  ) : paybills.map((item) => (
                    <div className="settingsPaymentRow" key={item.id}>
                      <div className="settingsPaymentRowGrid settingsPaymentRowGrid--two">
                        <Form.Group>
                          <Form.Label>Paybill number</Form.Label>
                          <Form.Control
                            type="text"
                            value={item.paybillNumber}
                            onChange={(event) => updatePaybill(item.id, 'paybillNumber', event.target.value)}
                            placeholder="e.g. 123456"
                          />
                        </Form.Group>
                        <Form.Group>
                          <Form.Label>Account / reference</Form.Label>
                          <Form.Control
                            type="text"
                            value={item.accountNumber}
                            onChange={(event) => updatePaybill(item.id, 'accountNumber', event.target.value)}
                            placeholder="e.g. ADM 2026"
                          />
                        </Form.Group>
                        <Form.Group className="settingsPaymentRowGrid--full">
                          <Form.Label>Note</Form.Label>
                          <Form.Control
                            type="text"
                            value={item.note}
                            onChange={(event) => updatePaybill(item.id, 'note', event.target.value)}
                            placeholder="Fee payment, savings, etc."
                          />
                        </Form.Group>
                      </div>
                      <Button
                        variant="outline-danger"
                        className="settingsPaymentRemoveBtn"
                        onClick={() => removePaybill(item.id)}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="settingsPaymentSection">
                <div className="settingsPaymentSectionHeader">
                  <div>
                    <h6>Till Numbers</h6>
                    <p>Use for till-based collections, quick deposits, and mobile money intake.</p>
                  </div>
                  <Button variant="outline-dark" size="sm" onClick={addTill}>
                    Add till
                  </Button>
                </div>

                <div className="settingsPaymentList">
                  {tills.length === 0 ? (
                    <div className="settingsEmptyState settingsEmptyState--compact">
                      No till numbers added yet.
                    </div>
                  ) : tills.map((item) => (
                    <div className="settingsPaymentRow" key={item.id}>
                      <div className="settingsPaymentRowGrid settingsPaymentRowGrid--two">
                        <Form.Group>
                          <Form.Label>Till number</Form.Label>
                          <Form.Control
                            type="text"
                            value={item.tillNumber}
                            onChange={(event) => updateTill(item.id, 'tillNumber', event.target.value)}
                            placeholder="e.g. 889900"
                          />
                        </Form.Group>
                        <Form.Group className="settingsPaymentRowGrid--full">
                          <Form.Label>Note</Form.Label>
                          <Form.Control
                            type="text"
                            value={item.note}
                            onChange={(event) => updateTill(item.id, 'note', event.target.value)}
                            placeholder="Fee payment, savings, etc."
                          />
                        </Form.Group>
                      </div>
                      <Button
                        variant="outline-danger"
                        className="settingsPaymentRemoveBtn"
                        onClick={() => removeTill(item.id)}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="settingsActions">
              <Button variant="dark" onClick={savePaymentDetails} disabled={savingPaymentDetails}>
                {savingPaymentDetails ? 'Saving...' : 'Save fee payment details'}
              </Button>
            </div>
          </div>
          </section>

          <section
            className="settingsTabPanel"
            id="settings-panel-security"
            role="tabpanel"
            aria-labelledby="settings-tab-security"
            hidden={activeSection !== 'security'}
          >
            <MpesaSecurityCredentialSettings />
          </section>
        </>
      )}
    </div>
  );
}

export default SettingsComp;
